import { DocumentTextIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { EOrganization, ESnapshotExists, EUser } from 'lib/types';
import { useFirestoreQueryListener } from 'lib/frontend/hooks/useFirestoreQueryListener';
import { getFirebaseContext } from 'utils/firebase';
import { ColumnButton } from 'lib/components/ColumnButton';
import { userIsSuper } from 'utils/permissions';
import SettingsPage from 'routes/settings/SettingsPage';
import { isColumnUser } from 'lib/helpers';
import ImplementationInfo from './ImplementationInfo';
import SettingsHeader from '../../SettingsHeader';
import PublisherInfo from './PublisherInfo';
import PublisherInfoDrawer from './PublisherInfoDrawer';
import AdjudicationInfo from './AdjudicationInfo/AdjudicationInfo';

type PublisherSettingsInfoProps = {
  activeOrganization: ESnapshotExists<EOrganization>;
  user: ESnapshotExists<EUser>;
};

/**
 * Control panel for editing basic information about the publisher
 */
export default function PublisherSettingsInfo({
  activeOrganization,
  user
}: PublisherSettingsInfoProps) {
  const [showNotes, setShowNotes] = useState(false);

  const publisherNotesQuery = useFirestoreQueryListener(
    getFirebaseContext()
      .notesRef()
      .where('publisher', '==', activeOrganization.ref)
      .where('noteCreatorOrganization', '==', activeOrganization.ref),
    [activeOrganization.id]
  );
  const numberOfActiveNotes =
    publisherNotesQuery?.docs.filter(note => note.data().status !== 'archived')
      .length || 0;

  return (
    <>
      <SettingsPage>
        <SettingsHeader
          header="Publisher Info"
          description="Tell us a bit about your publication."
        >
          {isColumnUser(user) && (
            <div className="mr-3">
              <ColumnButton
                startIcon={<DocumentTextIcon className="w-5 h-5" />}
                onClick={() => setShowNotes(true)}
                buttonText={
                  numberOfActiveNotes
                    ? `Show Notes (${numberOfActiveNotes})`
                    : 'Show Notes'
                }
                id="show-notes"
                type="button"
              />
            </div>
          )}
        </SettingsHeader>
        <PublisherInfo />
        {userIsSuper(user) && <ImplementationInfo />}
        <AdjudicationInfo />
      </SettingsPage>
      {showNotes && publisherNotesQuery && (
        <PublisherInfoDrawer
          activeOrganization={activeOrganization}
          publisherNotes={publisherNotesQuery?.docs}
          onClose={() => setShowNotes(false)}
        />
      )}
    </>
  );
}
